import type { OnboardingStep, OnboardingProgress } from '../hooks/useOnboarding'

interface OnboardingWizardProps {
  steps: OnboardingStep[]
  progress: OnboardingProgress
}

const STEP_KEYS = ['fiscal_period', 'chart_of_accounts', 'first_transaction', 'completed']

export default function OnboardingWizard({ steps, progress }: OnboardingWizardProps) {
  const currentStepIndex = STEP_KEYS.indexOf(progress.current_step)

  const stepStatus = (stepKey: string): 'completed' | 'active' | 'upcoming' => {
    const stepIndex = STEP_KEYS.indexOf(stepKey)
    if (stepIndex < currentStepIndex) return 'completed'
    if (stepIndex === currentStepIndex) return 'active'
    return 'upcoming'
  }

  const displaySteps = steps.filter(s => s.key !== 'completed')

  return (
    <div className="w-full">
      {/* Progress bar */}
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-medium text-gray-600">Aloitusprosessi</span>
        <span className="text-sm text-gray-500">{progress.progress_percent}% valmis</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
        <div
          className="bg-blue-600 h-2 rounded-full transition-all duration-500"
          style={{ width: `${progress.progress_percent}%` }}
        />
      </div>

      {/* Step indicators */}
      <div className="flex items-center">
        {displaySteps.map((step, idx) => {
          const status = stepStatus(step.key)
          return (
            <div key={step.key} className="flex items-center flex-1">
              {/* Step circle */}
              <div className="flex flex-col items-center min-w-0 flex-1">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors
                    ${status === 'completed' ? 'bg-green-500 text-white' :
                      status === 'active' ? 'bg-blue-600 text-white ring-4 ring-blue-100' :
                      'bg-gray-200 text-gray-500'}`}
                >
                  {status === 'completed' ? '✓' : idx + 1}
                </div>
                <span
                  className={`mt-1 text-xs text-center leading-tight max-w-[80px]
                    ${status === 'active' ? 'text-blue-700 font-medium' :
                      status === 'completed' ? 'text-green-700' :
                      'text-gray-400'}`}
                >
                  {step.title}
                </span>
              </div>

              {/* Connector line */}
              {idx < displaySteps.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-1 -mt-5 transition-colors
                    ${STEP_KEYS.indexOf(step.key) < currentStepIndex ? 'bg-green-400' : 'bg-gray-200'}`}
                />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
